import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { ParsedFilter } from '../filters/filter-util';
import type { BookmarkDto, BookmarkFolder } from './bookmarkDto';

export enum NotificationFlag {
  DesktopDragTip = 'DesktopDragTip',
  WildcardAndOperatorMix = 'Infobox.WildcardAndOperatorMix',
  SubfolderSearch = 'Infobox.SubfolderSearch',
  LoadAllButtonMoved = 'Infobox.LoadAllButtonMoved',
}

/**
 * User bookmarks, as they are stored in user settings bucket
 */
export interface Bookmark {
  id: string | undefined;
  icon: string;
  title: string;
  path: string;
  folderTreePath: string | undefined;
  isFilterBookmark: boolean | undefined;
  /**
   * Contains the parent folder id. If undefined, the bookmark is a root bookmark.
   */
  parentFolder: string | undefined;
    /**
   * Lexical ordering key for sorting bookmarks.
   */
  sortKey: string | undefined;
}
export interface BookmarkViewModel extends Bookmark {
  id: string; // Needed for vue reactivity. Injected client side
  type: 'user'; // Needed to be able to distinguish between system bookmarks and user bookmarks.
}

export type BookmarkModel = BookmarkViewModel | BookmarkDto;
export const BM_QUERY_PARAM = 'bId';

export const isBookmarkViewModel = (object: unknown): object is BookmarkViewModel => {
  const maybeBookmarkViewModel = object as BookmarkViewModel;
  return maybeBookmarkViewModel.id !== undefined && maybeBookmarkViewModel.title !== undefined && ('type' in maybeBookmarkViewModel) && !('createdBy' in maybeBookmarkViewModel);
};

export type Theme = 'light'| 'dark'| 'os';
export type Language = 'en'| 'fr' | 'nb';

export const defaultSubtitleOptions = <const>['transcript', 'translation', 'Off'];

export type SubtitleDefaultOptions = typeof defaultSubtitleOptions[number];

export interface UserSettings {
  bookmarks: Bookmark[];
  bookmarksFolders: BookmarkFolder[] | undefined;
  lastSeenChangelogId?: string;
  workFolderId?: string;
  // TODO(dorjoy): Remove when removing 'showSetFiltersAsDefault' feature flag
  defaultFilters?: ParsedFilter[];
  dismissedNotifications?: Record<string, boolean>;
  /**
   * Custom notifications that the user marked as read. Keys are the IDs
   */
  markedAsReadUserNotifications?: Record<string, boolean>;
  showRenameSubclipDialog?: boolean;
  showSequenceParts?: boolean;
  language?: Language;
  theme?: Theme;
  subtitlePreference?: SubtitleDefaultOptions;
  showPopups?: boolean;
  /**
   * A list of item IDs pinned by the user for quick access.
   */
  pinnedItems?: string[];
}

export interface EditBookmarkParams {
  id: string;
  icon: string;
  title: string;
  folderTreePath: string | undefined;
  path: string;
  parentFolder: string | undefined;
  sortKey: string | undefined;
}

export interface UserAvatarUploadUrlRequest {
  contentType: string;
  fileName: string;
}

export interface UserAvatarUploadDto {
  uploadUrl: string;
}

export const isCreateAvatarUploadURLRequest = (obj: unknown): obj is UserAvatarUploadUrlRequest => {
  if (!isObject(obj)) return false;
  const probablyUploadUrlRequest = obj as Partial<UserAvatarUploadUrlRequest>;
  return typeof probablyUploadUrlRequest.contentType === 'string' && typeof probablyUploadUrlRequest.fileName === 'string';
};
